"use client";

import { useEffect, useRef, useState } from "react";
import { getActivity } from "@/lib/activity-log";
import { useModal } from "@/components/ui/ModalProvider";
import BuggIkon from "@/components/layout/BuggIkon";

// Bug-ikon i nav-headern + popover-panel som glider ut höger-aligned under
// ikonen. Panelen är ~480 px bred, max ~80vh hög, och ligger ovanpå sidan
// med subtil skugga — inte modal/overlay, så användaren ser fortfarande
// resten av sidan och kan påminna sig om vad de höll på med.
//
// Klick utanför panelen stänger. Esc stänger. Submit POSTar till
// /api/bug-report med insamlad context (URL + activity-log + userAgent +
// ev. screenshot som base64). Användaren ser INTE den gömda context-datan.

const MAX_SCREENSHOT_BYTES = 2 * 1024 * 1024;

const SCREENSHOT_INSTRUKTION = {
  mac: "Mac: Cmd + Shift + 4 → markera område → fil hamnar på skrivbordet.",
  pc: "Windows: Win + Shift + S → markera område → klistra in eller spara från notiset.",
};

type Status = "idle" | "skickar" | "klar" | "fel" | "rate_limit";

interface TooLargeFile {
  name: string;
  sizeMb: string;
}

export default function BugReportButton() {
  const { confirm } = useModal();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  // Det enda fältet — skickas som `steps`, som API:et redan kräver (övriga
  // textfält är valfria där och skickas inte längre).
  const [beskrivning, setBeskrivning] = useState("");
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [tooLarge, setTooLarge] = useState<TooLargeFile | null>(null);
  const [rateLimitMsg, setRateLimitMsg] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Klick utanför + Esc → stäng
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    // Lägg till lyssnaren med setTimeout så vi inte fångar samma klick
    // som öppnade panelen.
    const t = window.setTimeout(() => {
      document.addEventListener("click", onClick);
    }, 0);
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function handleFile(file: File) {
    setTooLarge(null);
    if (file.size > MAX_SCREENSHOT_BYTES) {
      setScreenshot(null);
      setTooLarge({
        name: file.name,
        sizeMb: (file.size / 1024 / 1024).toFixed(1),
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setScreenshot(reader.result as string);
    };
    reader.onerror = () => {
      setTooLarge({ name: file.name, sizeMb: "?" });
    };
    reader.readAsDataURL(file);
  }

  function clearTooLarge() {
    setTooLarge(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!beskrivning.trim()) return;

    if (tooLarge) {
      const proceed = await confirm({
        title: "Bilden du bifogat är för stor",
        message: `${tooLarge.name} är ${tooLarge.sizeMb} MB (max 2 MB). Vill du skicka in rapporten utan bilden?`,
        confirmLabel: "Skicka in utan bild",
        cancelLabel: "Avbryt",
      });
      if (!proceed) return;
      clearTooLarge();
    }

    setStatus("skickar");
    try {
      const res = await fetch("/api/bug-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: window.location.href,
          steps: beskrivning,
          screenshotData: screenshot,
          activityLog: getActivity(),
          userAgent: navigator.userAgent,
        }),
      });
      if (res.status === 429) {
        const data = (await res.json().catch(() => null)) as
          | { message?: string }
          | null;
        setRateLimitMsg(
          data?.message ?? "För många rapporter — försök igen senare"
        );
        setStatus("rate_limit");
        return;
      }
      if (!res.ok) {
        setStatus("fel");
        return;
      }
      setStatus("klar");
      setBeskrivning("");
      setScreenshot(null);
      // Stäng efter kort fördröjning så användaren ser bekräftelsen
      window.setTimeout(() => {
        setOpen(false);
        setStatus("idle");
      }, 2500);
    } catch {
      setStatus("fel");
    }
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Rapportera eller ge feedback"
        title="Rapportera ett fel, orättvis rättning, feedback eller ett förbättringsförslag"
        className="-ml-2 p-2 rounded-md text-[var(--color-nav-text)] hover:bg-[var(--color-nav-border)] transition-colors"
      >
        <BuggIkon />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Rapportera eller ge feedback"
          className="absolute right-0 top-full mt-2 w-[28rem] max-w-[90vw] rounded-lg border shadow-2xl z-40 overflow-hidden"
          style={{
            background: "var(--color-bg-card)",
            borderColor: "var(--color-border)",
            maxHeight: "80vh",
          }}
        >
          <div
            className="px-4 py-3 border-b flex items-center justify-between"
            style={{ borderColor: "var(--color-border)" }}
          >
            <h2 className="text-sm font-semibold text-[var(--color-text)]">
              Säg till oss
            </h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Stäng"
              title="Stäng utan att skicka"
              className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] text-lg leading-none"
            >
              ✕
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="overflow-y-auto px-4 py-3 space-y-3"
            style={{ maxHeight: "calc(80vh - 60px)" }}
          >
            <p className="text-sm text-[var(--color-text)] leading-relaxed">
              Här kan du rapportera vad som helst: något som inte fungerar, en
              rättning som känns orättvis, allmän feedback eller en idé om hur
              vektr kan bli bättre.
            </p>

            <label className="block">
              <span className="block text-sm font-medium text-[var(--color-text)] mb-1">
                Beskriv vad det gäller
              </span>
              <textarea
                value={beskrivning}
                onChange={(e) => setBeskrivning(e.target.value)}
                rows={5}
                required
                placeholder="Till exempel: I övning 4 fick jag fel fast mitt svar var 12, samma som facit."
                className="w-full px-3 py-2 rounded-sm border border-[var(--color-border)] bg-[var(--color-bg)] text-sm leading-relaxed text-[var(--color-text)] focus:outline-2 focus:outline-offset-2 focus:outline-[var(--color-accent)]"
              />
              <span className="block mt-1 text-xs text-[var(--color-text-muted)]">
                Vi ser automatiskt vilken sida du var på.
              </span>
            </label>

            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
              />
              {!screenshot ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  title="Välj en bild eller skärmdump att skicka med (valfritt)"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-[var(--color-border-strong)] text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-border)]/40 transition-colors"
                >
                  <svg aria-hidden="true" viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <circle cx="9" cy="10" r="1.75" />
                    <path d="M21 16l-5-5-9 9" />
                  </svg>
                  Bifoga bild (valfritt)
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <img
                    src={screenshot}
                    alt="Bilden du bifogar"
                    className="h-16 w-auto max-w-[8rem] object-cover rounded-sm border border-[var(--color-border)]"
                  />
                  <div className="flex flex-col gap-1 text-sm">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      title="Välj en annan bild i stället"
                      className="text-left text-[var(--color-text)] underline underline-offset-2 hover:opacity-80"
                    >
                      Byt bild
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setScreenshot(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      title="Skicka utan bild"
                      className="text-left text-[var(--color-accent)] underline underline-offset-2 hover:opacity-80"
                    >
                      Ta bort bilden
                    </button>
                  </div>
                </div>
              )}
              {tooLarge && (
                <div className="mt-2 flex items-start gap-2 rounded-sm border border-[var(--color-accent)] bg-[var(--color-accent-soft)] px-3 py-2">
                  <div className="flex-1 text-xs text-[var(--color-text)] leading-snug">
                    {tooLarge.name} är {tooLarge.sizeMb} MB, men bilden får vara
                    högst 2 MB. Välj en mindre bild, eller skicka utan.
                  </div>
                  <button
                    type="button"
                    onClick={clearTooLarge}
                    aria-label="Ta bort den för stora bilden"
                    title="Ta bort den för stora bilden så du kan välja en mindre"
                    className="text-[var(--color-accent)] hover:opacity-70 leading-none text-base font-bold px-1"
                  >
                    ✕
                  </button>
                </div>
              )}
              <p className="mt-2 text-xs text-[var(--color-text-muted)] leading-snug">
                Skärmdump: {SCREENSHOT_INSTRUKTION.mac}
                <br />
                {SCREENSHOT_INSTRUKTION.pc}
              </p>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <button
                type="submit"
                disabled={!beskrivning.trim() || status === "skickar"}
                title="Skicka in det du skrivit till vektr"
                className="text-sm font-medium px-4 py-2 rounded-md bg-[var(--color-text)] text-[var(--color-bg)] transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === "skickar" ? "Skickar…" : "Skicka in"}
              </button>
              {status === "klar" && (
                <span className="text-sm text-[var(--color-success)]">
                  Tack! Vi har fått det du skrev.
                </span>
              )}
              {status === "fel" && (
                <span className="text-xs text-[var(--color-figur-rost)]">
                  Det gick inte att skicka. Försök igen om en stund.
                </span>
              )}
              {status === "rate_limit" && rateLimitMsg && (
                <span className="text-xs text-[var(--color-figur-rost)]">
                  {rateLimitMsg}
                </span>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
